import { ErrorModal, useContextMenuMESF } from "@dexteel/mesf-core";
import { Button, Grid } from "@material-ui/core";
import {
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GridApi,
	IRowNode,
	MenuItemDef,
	RowSelectedEvent,
} from "ag-grid-community";

import { AgGridReact } from "ag-grid-react";
import React, { useEffect, useState } from "react";

import { CenteredLazyLoading } from "@/controls/CenteredLazyLoading";
import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { SearchTextField } from "@/controls/quick-filter-textfield/SearchTextField";
import { useGridStyles } from "@/pages/laboratory/styles/gridStyles";
import { CatalogGrade } from "@/pages/steel/models/CatalogGrade";
import PlaylistAddIcon from "@material-ui/icons/PlaylistAdd";
import { useNavigate } from "react-router-dom";
import { useGradeOptionsFunctions } from "../hooks/useGradeOptionsFunctions";
import { useTableData } from "../hooks/useTableData";
import { DeleteGradeModal } from "../modals/DeleteGradeModal";
import { GradeDetails } from "./GradeDetails";

type Props = {
	isLoading: boolean;
	getCatalogGradesFromAPI: Function;
	gradeId: string | undefined;
};

export const TableGrade = ({
	isLoading,
	getCatalogGradesFromAPI,
	gradeId,
}: Props) => {
	const classes = useGridStyles();
	const navigate = useNavigate();

	const [filterValue, setFilterValue] = useState("");
	const [error, setError] = useState("");
	const [selectedRowData, setSelectedRowData] = useState<CatalogGrade | null>(
		null,
	);
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [showModal, setShowModal] = useState<
		"create" | "update" | "delete" | ""
	>("");

	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const handleNewGrade = () => {
		if (gridApi) gridApi.deselectAll();
		setShowModal("create");
		setSelectedRowData(null);
		navigate(`/configuration/steel-grade/new`);
	};
	const { getMenuOptions } = useGradeOptionsFunctions({
		setSelectedRowData,
		setShowModal,
		handleNewGrade,
	});
	const { rows, columnDefs } = useTableData({
		setSelectedRowData,
		setShowModal,
		showContextMenu,
	});

	const { columnSimpleDefaults } = useGridDefinitions({
		OnEdit: (data: any) => {
			setShowModal("update");
			setSelectedRowData(data);
		},
	});

	const onRowSelected = (event: RowSelectedEvent) => {
		const cell = gridApi?.getFocusedCell();
		if (cell?.column.getColId() !== "-" && event.node.isSelected()) {
			setSelectedRowData(event.data);
			setShowModal("update");
			navigate(
				`/configuration/steel-grade/${(event.data as CatalogGrade).GradeId}`,
			);
		}
	};

	const getContextMenuItems = React.useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const data = params.node?.data;
			showContextMenu(event as any, data, "gradeContext");
			if (data) {
				params.api.deselectAll();
				params.node?.setSelected(true);
			}
			return [];
		},
		[rows],
	);

	const onModalHide = ({
		shouldUpdate,
		close,
		entityId,
	}: {
		shouldUpdate: boolean;
		close: boolean;
		entityId?: number;
	}) => {
		if (close) {
			setShowModal("");
			setSelectedRowData(null);
			if (gridApi) gridApi?.deselectAll();
			return;
		}
		if (entityId && shouldUpdate) {
			setTimeout(() => {
				const rowNode = gridApi?.getRowNode(String(entityId));

				const pageSize = gridApi?.paginationGetPageSize();
				const pageNumber = Math.floor((rowNode?.rowIndex ?? 0) / pageSize!);
				gridApi?.paginationGoToPage(pageNumber);

				rowNode?.setSelected(true);
				setSelectedRowData(rowNode?.data);
			}, 0);
		}
	};

	useEffect(() => {
		if (isLoading) {
			gridApi?.showLoadingOverlay();
		} else {
			gridApi?.hideOverlay();
			if (gradeId) {
				if (gradeId === "new") {
					handleNewGrade();
				} else {
					let partNode: IRowNode<any>;

					gridApi?.forEachNode((n) => {
						if ((n.data as CatalogGrade).GradeId === Number(gradeId)) {
							partNode = n;
							return true;
						}
					});

					if (partNode!) {
						const pageSize = gridApi?.paginationGetPageSize();
						const pageNumber = Math.floor(
							(partNode!.rowIndex ?? 0) / pageSize!,
						);
						gridApi?.paginationGoToPage(pageNumber);

						partNode.setSelected(true);
						setSelectedRowData(partNode.data);
						setShowModal("update");
					}
				}
			}
		}
	}, [isLoading, gridApi]);

	useEffect(() => {
		registerConfig({
			id: "gradeContext",
			getOptions: getMenuOptions,
		});
	}, []);

	return (
		<>
			<Grid item container justifyContent="flex-start">
				<Grid item md={12} xs={12} style={{ marginBottom: 10 }}>
					<SearchTextField
						value={filterValue}
						onChange={(value) => setFilterValue(value)}
						label="Search grades"
					/>
				</Grid>

				<Grid
					item
					md={4}
					xs={12}
					justifyContent="flex-end"
					className={classes.root}
					container
					style={{ height: "68vh" }}
				>
					<Grid
						item
						xs={12}
						style={{ height: "92%" }}
						className={classes.borderAgGridTable}
					>
						<AgGridReact<CatalogGrade>
							rowData={rows || []}
							columnDefs={columnDefs}
							defaultColDef={columnSimpleDefaults}
							rowHeight={35}
							headerHeight={35}
							animateRows
							rowSelection="single"
							paginationAutoPageSize
							getContextMenuItems={getContextMenuItems}
							onGridReady={(params) => setGridApi(params.api)}
							getRowId={(params) => String(params.data.GradeId)}
							loading={isLoading}
							onRowSelected={onRowSelected}
							quickFilterText={filterValue}
						/>
					</Grid>
					<Grid item md={12} xs={12} style={{ marginTop: 10 }}>
						<Button
							variant="contained"
							color="primary"
							fullWidth
							style={{ minWidth: 250 }}
							onClick={handleNewGrade}
							endIcon={<PlaylistAddIcon />}
						>
							NEW GRADE
						</Button>
					</Grid>
				</Grid>
				<Grid
					item
					container
					md={7}
					xs={12}
					className={classes.borderAgGridTable}
					style={{ marginLeft: "20px" }}
				>
					<GradeDetails
						grade={selectedRowData}
						modal={showModal}
						refreshData={getCatalogGradesFromAPI}
						onHide={onModalHide}
					/>
				</Grid>
			</Grid>

			<ErrorModal error={error} onHide={() => setError("")} />
			<DeleteGradeModal
				show={showModal}
				onHide={(shouldUpdate: boolean) => {
					if (shouldUpdate) {
						getCatalogGradesFromAPI();
					}
					setSelectedRowData(null);
					setShowModal("");
				}}
				selectedRowData={selectedRowData}
			/>
		</>
	);
};
